import styled from "@emotion/styled"
import { breakpoints } from "../../theme/mediaQueries"
import { colors } from "../../theme/colors"
import { sizes } from "../../theme/sizes"
import { typographyVariants } from "../../theme/typography"
import { richTextListStyles } from "../../common/richTextListStyles"

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
  }
`

export const Strip = styled.div<{ $hasImage: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  background-color: ${(p) =>
    p.$hasImage ? "transparent" : colors.primitives.secondary200};
  border-radius: ${sizes.borderRadius[24]}px;
  padding: ${sizes.spacing[48]}px ${sizes.spacing[16]}px;
  /* EGDE-657: the fixed band height exists to give the background photo room.
     With no image there is nothing to fill, so a short title left a mostly empty
     400/540px block of flat colour — the designer's two states are "no
     background image: the container height shrinks according to the inner
     templates" and "an image is defined: the height remains as it is". The
     padding still sets the breathing room, and Content/TextGroup keep their
     flex-grow so the image state centres exactly as before (EGDE-312). */
  ${(p) => (p.$hasImage ? "height: 400px;" : "")}

  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${sizes.borderRadius[40]}px;
    padding: ${sizes.spacing[48]}px;
    ${(p) => (p.$hasImage ? "height: 540px;" : "")}
  }

  @media (min-width: ${breakpoints.xxl}px) {
    ${(p) => (p.$hasImage ? "height: 540px;" : "")}
    padding: ${sizes.spacing[64]}px;
  }
`

/* EGDE-599: the hero honours the responsiveImage Mobile Image. Toggled in CSS
   rather than with a viewport hook so the server and client markup match (see
   HeroBanner for the same pattern). */
export const DesktopLayer = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileLayer = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* EGDE-659: QA "the inner hero image appears a lot darker than it is
   originally" (it was a flat black 40%). The designer's updated mask is two
   fills over the photo, and the ticket notes it cannot be lightened further
   without risking contrast:
     Linear  8%  — brand orange, fading ACROSS the strip
     000000  32% — flat
   The orange is strongest on the side the copy sits on and fades to nothing at
   the far edge, so the title/CTA keep a little extra backing while the rest of
   the photo opens up.
   Direction is written out per dir instead of relying on a logical value:
   stylis-plugin-rtl does NOT mirror gradient ANGLES (verified in the browser —
   an authored 90deg stays 90deg on the he stylesheet), so a single declaration
   would push the orange to the far side from the text on Hebrew and Arabic. The
   270deg base is Figma's own value for the Hebrew design (orange on the right,
   where RTL copy sits); [dir="ltr"] mirrors it, the same way the chevron bullet
   is mirrored in richTextListStyles.
   Stop percentages: only the midpoint carries one. The alpha holds at 0.072 to
   halfway before falling away, so that stop is load-bearing — but 50% survives a
   flip, whereas explicit 0%/100% stops get inverted by stylis in RTL and
   production strips @noflip. */
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.32);
  background-image: linear-gradient(
    270deg,
    rgba(248, 111, 62, 0.08),
    rgba(248, 111, 62, 0.072) 50%,
    rgba(248, 111, 62, 0)
  );
  z-index: 1;

  [dir="ltr"] & {
    background-image: linear-gradient(
      90deg,
      rgba(248, 111, 62, 0.08),
      rgba(248, 111, 62, 0.072) 50%,
      rgba(248, 111, 62, 0)
    );
  }
`

export const Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  width: 100%;
  /* EGDE-312: fill the strip height so the text can sit centered while the CTA
     is pinned to the bottom (with the strip's bottom padding below it),
     independent of how much copy there is. */
  flex: 1;
`

export const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  /* EGDE-312: grow to fill the space above the CTA and vertically center the
     title/subtitle within it, so the text stays centered regardless of length
     while the CTA sits at the bottom. */
  flex: 1;
  justify-content: center;
`

/* EGDE-726: the inner hero owns the page's H1, so this renders as a real
   <h1>. The CMS title is rich text whose block tags (p, pasted headings) are
   flattened to <span>s by the component before injection — phrasing content is
   all an h1 may contain — so the spans carry the line breaks instead. */
export const TitleText = styled.h1<{ $hasImage?: boolean }>`
  color: ${(p) =>
    p.$hasImage
      ? colors.primitives.neutral0
      : colors.light.text.primary};
  text-align: start;
  margin: 0;
  font-size: ${typographyVariants.h1.mobile.fontSize}px;
  line-height: ${typographyVariants.h1.mobile.lineHeight}px;
  font-weight: ${typographyVariants.h1.mobile.fontWeight};

  p,
  span {
    display: block;
    margin: 0;
  }

  /* EGDE-544: rich-text hyperlinks use the site-wide link blue (EGDE-327)
     instead of inheriting the surrounding text color. */
  a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.7;
    }
  }

  /* EGDE-543: chevron bullets / orange ordered markers, shared with every
     other rich-text renderer. */
  ${richTextListStyles}

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${typographyVariants.h1.desktop.fontSize}px;
    line-height: ${typographyVariants.h1.desktop.lineHeight}px;
    font-weight: ${typographyVariants.h1.desktop.fontWeight};
  }
`

export const SubtitleText = styled.div<{ $hasImage?: boolean }>`
  color: ${(p) =>
    p.$hasImage
      ? colors.primitives.neutral0
      : colors.light.text.primary};
  text-align: start;
  font-size: ${typographyVariants.body1.mobile.fontSize}px;
  line-height: ${typographyVariants.body1.mobile.lineHeight}px;
  font-weight: ${typographyVariants.body1.mobile.fontWeight};

  p {
    margin: 0;
  }

  /* EGDE-544: rich-text hyperlinks use the site-wide link blue (EGDE-327)
     instead of inheriting the surrounding text color. */
  a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.7;
    }
  }

  /* EGDE-543: chevron bullets / orange ordered markers, shared with every
     other rich-text renderer. */
  ${richTextListStyles}

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${typographyVariants.body1.desktop.fontSize}px;
    line-height: ${typographyVariants.body1.desktop.lineHeight}px;
    font-weight: ${typographyVariants.body1.desktop.fontWeight};
  }
`

export const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[24]}px;
  min-width: 200px;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  color: ${colors.light.text.primary};
  font-size: ${typographyVariants.body1.desktop.fontSize}px;
  line-height: ${typographyVariants.body1.desktop.lineHeight}px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`
